/**
 * Hilfen für die Arbeitsschritt-Animationen in der Oberfläche: Palette aus
 * den CSS-Variablen, Einstellungen des Systems und Sichtbarkeit.
 */
import { DARK_PALETTE, LIGHT_PALETTE, TONES } from '@cookbook/animations';
import type { Palette } from '@cookbook/animations';
import { useEffect, useMemo, useState } from 'react';
import type { RefObject } from 'react';
import { useIsDark } from '../state/appearance.ts';
import { usePrinting } from './print.ts';

/** Name der CSS-Variable einer Tonstufe, zum Beispiel --anim-paper. */
function variableName(tone: string): string {
  return `--anim-${tone.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)}`;
}

/** Liest die Palette aus den CSS-Variablen; fehlende Töne kommen aus der Vorgabe. */
function readPalette(dark: boolean): Palette {
  const fallback = dark ? DARK_PALETTE : LIGHT_PALETTE;
  if (typeof window === 'undefined') {
    return fallback;
  }
  const style = window.getComputedStyle(document.documentElement);
  const entries = TONES.map((tone) => {
    const value = style.getPropertyValue(variableName(tone)).trim();
    return [tone, value === '' ? fallback[tone] : value] as const;
  });
  return Object.fromEntries(entries) as Palette;
}

/** Abfrage einer Medienbedingung als Zustand, der sich mit dem System ändert. */
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window === 'undefined' ? false : window.matchMedia(query).matches,
  );
  useEffect(() => {
    const list = window.matchMedia(query);
    const update = () => setMatches(list.matches);
    update();
    list.addEventListener('change', update);
    return () => list.removeEventListener('change', update);
  }, [query]);
  return matches;
}

/** Wünscht das System weniger Bewegung? */
export function usePrefersReducedMotion(): boolean {
  return useMediaQuery('(prefers-reduced-motion: reduce)');
}

/** Palette passend zum Hell- oder Dunkelmodus (System oder feste Wahl). */
export function useAnimationPalette(): Palette {
  // Gedruckt wird immer hell.
  const printing = usePrinting();
  const dark = useIsDark() && !printing;
  // Sofort berechnet, nicht erst in einem Effekt: Vor dem Druck muss die
  // helle Palette schon im selben Durchlauf stehen.
  return useMemo(() => readPalette(dark), [dark]);
}

/** Ist das Element im sichtbaren Bereich und die Seite im Vordergrund? */
export function useIsVisible(ref: RefObject<Element | null>): boolean {
  const [inView, setInView] = useState(true);
  const [pageVisible, setPageVisible] = useState(() =>
    typeof document === 'undefined' ? true : document.visibilityState === 'visible',
  );
  useEffect(() => {
    const element = ref.current;
    if (element === null || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }
    const observer = new IntersectionObserver((entries) => {
      setInView(entries.some((entry) => entry.isIntersecting));
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);
  useEffect(() => {
    const update = () => setPageVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', update);
    return () => document.removeEventListener('visibilitychange', update);
  }, []);
  return inView && pageVisible;
}
